export const MCP_SCOPES = {
  clientsRead: "clients:read",
  clientsWrite: "clients:write",
  billingRead: "billing:read",
  assistantsRead: "assistants:read",
  assistantsWrite: "assistants:write",
} as const;

export const GRANT_CLOCK_SKEW_SECONDS = 30;

export const ALL_MCP_SCOPES: string[] = Object.values(MCP_SCOPES);

export const TEAM_MCP_SCOPES: string[] = [MCP_SCOPES.clientsRead, MCP_SCOPES.clientsWrite, MCP_SCOPES.billingRead];

export const WORKSPACE_MCP_SCOPES: string[] = [MCP_SCOPES.assistantsRead, MCP_SCOPES.assistantsWrite];

export type ToolRequirement =
  | { kind: "super_admin" }
  | { kind: "permission"; key: string };

export interface Caller {
  userId: string;
  clientId: string;
  grantedScopes: Set<string>;
  isSuperAdmin: boolean;
  permissions: Set<string>;
  organizationId?: string | null;
  grantedAt?: number | null;
}

export class ToolRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ToolRefusal";
  }
}

export interface ToolContext {
  accessToken: string;
}

export interface ToolAnnotations {
  title?: string;
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
}

// deno-lint-ignore no-explicit-any
export interface McpTool<Args = any> {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  scopes: string[];
  requires: ToolRequirement;
  annotations?: ToolAnnotations;
  handler: (args: Args, caller: Caller, context: ToolContext) => Promise<unknown>;
}

function meetsRequirement(requires: ToolRequirement, caller: Caller): boolean {
  if (requires.kind === "super_admin") return caller.isSuperAdmin;
  return caller.isSuperAdmin || caller.permissions.has(requires.key);
}

export function canCall(tool: McpTool, caller: Caller): boolean {
  if (!tool.scopes.every((scope) => caller.grantedScopes.has(scope))) return false;
  return meetsRequirement(tool.requires, caller);
}

export function visibleTools(tools: McpTool[], caller: Caller): McpTool[] {
  return tools.filter((tool) => canCall(tool, caller));
}

export function describeTool(tool: McpTool) {
  return {
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    ...(tool.annotations ? { annotations: tool.annotations } : {}),
  };
}
